import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {LAZY_FOOTER_KEY, MaterialTableWrapper} from './MaterialTableWrapper';

/**
 * Incremental ("load more on scroll") wrapper around {@link MaterialTableWrapper}.
 *
 * It keeps the FULL, already-sorted dataset in `data` and renders only a growing prefix of it. As many
 * rows as fit the scroll viewport are shown first; if more remain, a loading sentinel row is appended
 * INSIDE the table body (so it scrolls with the content and shows up in the scrollbar). On scroll toward
 * the bottom the next `renderBatchSize` rows are appended. A change of `sortKey` resets back to the
 * initial (viewport-fit) prefix.
 *
 * IMPORTANT: material-table must NOT re-sort the slice (that would sort only the visible prefix), so the
 * caller passes `data` already sorted and neutralizes material-table's own column sort
 * (e.g. `customSort: () => 0`), driving sort changes via `sortKey` + new sorted `data`.
 */
type LazyMaterialTableProps = {
  /** Full, already-sorted dataset. Only a growing prefix is handed to material-table. */
  data: any[];
  /** How many rows to append each time the user scrolls to the bottom. */
  renderBatchSize?: number;
  /** Changing this resets the rendered prefix back to the initial viewport fit (e.g. `${field}:${dir}`). */
  sortKey?: string | number;
  /** Row height (px) used to convert the measured viewport height into a row count. Stable estimate is
   * preferred over measuring rows (offsetHeight is unreliable mid-layout). */
  estimatedRowHeight?: number;
  /** Initial rendered-row count, measured by the PARENT from its (layout-owning) scroll container before
   * this mounts. Should slightly OVER-estimate the viewport fit so the first render never under-fills (a
   * floating spinner + staged "load more"); measureFit then shrinks it to the exact fit. Falls back to a
   * small constant when omitted. */
  initialRenderCount?: number;
  // everything else is passed straight through to MaterialTableWrapper (columns, options, variant, …)
  [key: string]: any;
};

// A few extra rows beyond the strict viewport fit, so the sentinel starts safely below the fold.
const VIEWPORT_BUFFER_ROWS = 3;

// A small first-paint row count, replaced by the real viewport fit as soon as the scroll viewport is
// measured. Deliberately modest (NOT window-height-based): rendering ~window-worth of rows up front made
// the heavy material-table mount render ~3x more cells than the visible viewport needs.
const INITIAL_RENDER_GUESS = 16;

// The real vertical scroll viewport is the nearest ancestor that actually scrolls vertically
// (overflow-y auto/scroll). material-table hands us an inner, UNCONSTRAINED container whose clientHeight
// equals the full content height; the actual clipping happens higher up (the split panel / contentBox),
// so the row-fit must be measured there, not on the container material-table gives us.
function findVerticalViewport(start: HTMLElement | null): HTMLElement | null {
  let el = start;
  while (el && el !== document.body) {
    const oy = getComputedStyle(el).overflowY;
    if (oy === 'auto' || oy === 'scroll') return el;
    el = el.parentElement;
  }
  return null;
}

export function LazyMaterialTable(props: LazyMaterialTableProps) {
  const {
    data,
    renderBatchSize = 50,
    sortKey,
    estimatedRowHeight = 29,
    initialRenderCount,
    onScroll: onScrollProp,
    onTableRefSet: onTableRefSetProp,
    ...rest
  } = props;

  const scrollerRef = useRef<HTMLElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const rowHeightRef = useRef(estimatedRowHeight);
  const userGrewRef = useRef(false);
  // Auto-fit is allowed to grow past the viewport only AFTER the layout has settled (startup grace) — this
  // stops a transient unsettled-tall viewport from ballooning the (expensive) mount render. `userInteracted`
  // gates load-more so programmatic/restore scrolls during mount can't lock the count via `userGrewRef`.
  const startupDoneRef = useRef(false);
  const userInteractedRef = useRef(false);
  // Load-more is "armed" a beat after mount: dragging the native scrollbar fires NO wheel/touch/keydown
  // (only `scroll`), so the gesture markers can't detect it — the timed arm lets ANY scroll trigger
  // load-more once the mount-time programmatic/restore scroll window has passed.
  const loadMoreArmedRef = useRef(false);
  // The HORIZONTAL scroll container the spinner is centered against (separate from the vertical scroller),
  // plus the observer/listener that keep it centered, and a ref to positionSentinel for those callbacks.
  const hScrollerRef = useRef<HTMLElement | null>(null);
  const hObserverRef = useRef<ResizeObserver | null>(null);
  const hScrollHandlerRef = useRef<(() => void) | null>(null);
  const positionSentinelRef = useRef<() => boolean>(() => false);
  // First-paint count: the parent's pre-measured (slightly over-estimated) viewport fit when available, else
  // a small constant. measureFit then refines it (shrinks the over-estimate to the exact fit). NOT tied to
  // renderBatchSize (that is only the scroll increment).
  const [renderedCount, setRenderedCount] = useState(() =>
    initialRenderCount && initialRenderCount > 0 ? initialRenderCount : INITIAL_RENDER_GUESS
  );
  const fitCountRef = useRef(renderedCount);
  const dataLenRef = useRef(data.length);
  dataLenRef.current = data.length;
  const batchRef = useRef(renderBatchSize);
  batchRef.current = renderBatchSize;

  const hasMore = renderedCount < data.length;
  const renderedData = useMemo(() => {
    const slice = data.slice(0, renderedCount);
    return hasMore ? [...slice, {[LAZY_FOOTER_KEY]: true}] : slice;
  }, [data, renderedCount, hasMore]);

  // Fit the rendered prefix to the visible viewport. Called repeatedly (rAF + timeouts + ResizeObserver +
  // scroll) because the split-layout height settles a beat after mount AND material-table can swap the
  // scroll-container DOM node — so a single measurement is unreliable. Key rule: only TRUST clientHeight
  // when the content actually overflows it; an unsettled panel is momentarily tall and does NOT overflow,
  // so we skip it instead of ballooning the (expensive) mount render to a window-sized fit.
  const measureFit = useCallback(() => {
    if (userGrewRef.current) return;
    // Re-resolve the viewport from a live anchor every time (a cached node may be stale/detached after a
    // material-table re-render — a likely reason an early measurement was never corrected).
    const anchor = (document.querySelector('[data-lazy-sentinel]') ?? document.querySelector('table tbody tr')) as HTMLElement | null;
    const vp = findVerticalViewport(anchor);
    if (!vp) return;
    const ch = vp.clientHeight;
    if (ch < rowHeightRef.current * 2) return; // not laid out yet
    if (vp.scrollHeight > ch + 2) {
      // Content overflows → clientHeight is the real visible height → pin the exact fit (shrinks the
      // over-render). Capped at the dataset so we never request more rows than exist.
      const fit = Math.min(Math.ceil(ch / rowHeightRef.current) + VIEWPORT_BUFFER_ROWS, dataLenRef.current);
      fitCountRef.current = fit;
      setRenderedCount((c) => (c === fit ? c : fit));
    } else if (startupDoneRef.current) {
      // Content does NOT fill the viewport and the layout has settled → the viewport is genuinely tall and
      // we just have too few rows. Grow a little to fill it (bounded). Skipped during startup so an
      // unsettled-tall layout can't push a heavy over-render before it collapses to its real height.
      setRenderedCount((c) => (c < dataLenRef.current ? Math.min(c + VIEWPORT_BUFFER_ROWS, dataLenRef.current) : c));
    }
  }, []);

  // Keep the (full-width, colSpan) sentinel's spinner centered in the VISIBLE horizontal viewport rather
  // than in the whole table width, via a translateX. NOTE: a CSS transform DOES contribute to scrollable
  // overflow, so the translated sentinel is wrapped in an `overflow:hidden` clip in LazyFooterRow — without
  // it the translateX extended the table's scrollWidth (blank space right of the last column on scroll).
  const positionSentinel = useCallback(() => {
    const sentinel = (scrollerRef.current?.querySelector('[data-lazy-sentinel]') ??
      document.querySelector('[data-lazy-sentinel]')) as HTMLElement | null;
    if (!sentinel) return false;
    // material-table has SEPARATE vertical and horizontal scroll containers — center the spinner against
    // the HORIZONTAL one. Find it by overflow-x: the scroll container exists from mount (even before its
    // content overflows), which lets us OBSERVE it and re-center the instant columns widen — instead of
    // racing a bounded retry that gave up before the horizontal layout settled.
    let hs: HTMLElement | null = sentinel.parentElement;
    while (hs && hs !== document.body) {
      const ox = getComputedStyle(hs).overflowX;
      if (ox === 'auto' || ox === 'scroll') break;
      hs = hs.parentElement;
    }
    if (!hs || hs === document.body) {
      sentinel.style.transform = '';
      return false;
    }
    // Attach (once) a ResizeObserver to the TABLE content (whose width drives scrollWidth — the h-scroller's
    // own box stays fixed, so observing it wouldn't fire) plus a scroll listener for horizontal scrolling.
    // These keep the spinner centered regardless of when the layout settles or where the user scrolls.
    if (hs !== hScrollerRef.current) {
      if (hScrollerRef.current && hScrollHandlerRef.current) {
        hScrollerRef.current.removeEventListener('scroll', hScrollHandlerRef.current);
      }
      hObserverRef.current?.disconnect();
      hScrollerRef.current = hs;
      hScrollHandlerRef.current = () => positionSentinelRef.current();
      hs.addEventListener('scroll', hScrollHandlerRef.current, {passive: true});
      hObserverRef.current = new ResizeObserver(() => positionSentinelRef.current());
      const tableEl = sentinel.closest('table');
      if (tableEl) hObserverRef.current.observe(tableEl);
      hObserverRef.current.observe(hs); // also catch viewport resize
    }
    // Before the content overflows, the cell's own centering already centers it in view (offset 0).
    const offset = hs.scrollWidth > hs.clientWidth + 2 ? hs.scrollLeft + hs.clientWidth / 2 - hs.scrollWidth / 2 : 0;
    sentinel.style.transform = offset ? `translateX(${offset}px)` : '';
    return true;
  }, []);
  positionSentinelRef.current = positionSentinel;

  // Capture the real scroll viewport and OBSERVE its size, so the fit becomes correct as soon as the
  // viewport gets its constrained height (the split layout may settle a beat after mount) and stays
  // correct on window resize. The element comes from material-table's tableContainerDiv (where it
  // attaches its own scroll listener) and, as a fallback, from the scroll event target.
  const captureScroller = useCallback(
    (el: HTMLElement | null) => {
      // Resolve the real vertical viewport (the overflow-y ancestor), NOT the container material-table gives
      // us. We keep a ref to it (for sentinel lookup / scroll reset) and observe it for resizes.
      const vp = findVerticalViewport(el);
      if (!vp) return;
      measureFit();
      if (vp === scrollerRef.current) return;
      scrollerRef.current = vp;
      observerRef.current?.disconnect();
      observerRef.current = new ResizeObserver(() => {
        measureFit();
        positionSentinel();
      });
      observerRef.current.observe(vp); // fires immediately with the current size, and on later changes
    },
    [measureFit, positionSentinel]
  );

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      hObserverRef.current?.disconnect();
      if (hScrollerRef.current && hScrollHandlerRef.current) {
        hScrollerRef.current.removeEventListener('scroll', hScrollHandlerRef.current);
      }
    },
    []
  );

  // Only a REAL user gesture may lock the count via load-more (`userGrewRef`). Programmatic / restore /
  // layout-shift scrolls during mount do NOT fire these, so they can't freeze an inflated count.
  useEffect(() => {
    const mark = () => {
      userInteractedRef.current = true;
    };
    window.addEventListener('wheel', mark, {passive: true, capture: true});
    window.addEventListener('touchmove', mark, {passive: true, capture: true});
    window.addEventListener('keydown', mark, true);
    return () => {
      window.removeEventListener('wheel', mark, true);
      window.removeEventListener('touchmove', mark, true);
      window.removeEventListener('keydown', mark, true);
    };
  }, []);

  // End the startup grace once, after which auto-fit may grow to fill a genuinely tall viewport AND any
  // scroll (incl. the native scrollbar) may trigger load-more — by now the mount-time programmatic/restore
  // scroll has passed.
  useEffect(() => {
    const id = setTimeout(() => {
      startupDoneRef.current = true;
      loadMoreArmedRef.current = true;
      measureFit();
    }, 500);
    return () => clearTimeout(id);
  }, [measureFit]);

  // Re-measure the fit across several frames/timeouts after each render: the split layout settles a beat
  // after mount and material-table may swap the scroll node, so one measurement isn't enough. measureFit
  // no-ops unless the viewport actually overflows (or, post-startup, under-fills), so this is cheap.
  useEffect(() => {
    let stop = false;
    const run = () => {
      if (!stop) measureFit();
    };
    const raf = requestAnimationFrame(run);
    const timers = [50, 150, 300, 600].map((t) => setTimeout(run, t));
    return () => {
      stop = true;
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
    };
  }, [renderedData, measureFit]);

  // Re-center the spinner whenever the sentinel (re)appears. Poll (NOT requestAnimationFrame) until the
  // horizontal scroll container is found and observed: rendering the ~1800-cell table can take seconds, and
  // doesn't tick during that long synchronous commit, so a frame-bounded retry expired before the table
  // (and its scroll container) existed — leaving the spinner stuck off-center. A self-terminating setTimeout
  // poll survives the heavy render; once positionSentinel attaches the ResizeObserver + scroll listener,
  // those keep it centered (incl. when the columns widen later), so we stop polling.
  useEffect(() => {
    let stop = false;
    let tries = 0;
    const tick = () => {
      if (stop) return;
      const attached = positionSentinel();
      if (!attached && tries++ < 50) setTimeout(tick, 120); // ~6s budget for the table to finish rendering
    };
    tick();
    return () => {
      stop = true;
    };
  }, [renderedData, positionSentinel]);

  const handleTableRef = useCallback(
    (ref: {tableContainerDiv?: React.RefObject<HTMLDivElement>} | null) => {
      onTableRefSetProp?.(ref);
      captureScroller(ref?.tableContainerDiv?.current ?? null);
    },
    [captureScroller, onTableRefSetProp]
  );

  // Reset to the initial fit when the sort changes (forget any appended batches). Skip first mount.
  const didMount = useRef(false);
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    userGrewRef.current = false;
    setRenderedCount(fitCountRef.current);
    if (scrollerRef.current) scrollerRef.current.scrollTop = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortKey]);

  // Append the next batch from the scroll container's own metrics (robust: uses the real scroll event).
  // Loads when the bottom (where the sentinel sits) is about one row away — ~when the spinner appears.
  const handleScroll = useCallback(
    (event: Event) => {
      onScrollProp?.(event);
      const el = event.target as HTMLElement | null;
      if (!el) return;
      captureScroller(el);
      positionSentinel(); // keep the spinner centered in view while scrolling horizontally
      // Trigger load-more near the bottom once it's safe to: either a real gesture happened (wheel/touch/
      // keydown) OR the post-mount arm fired (catches the native scrollbar, which emits only `scroll`). This
      // keeps the mount-time programmatic/restore scroll from locking the count, while `scrollable` guards
      // the unconstrained-mount case where clientHeight == scrollHeight falsely reads as "at the bottom".
      const scrollable = el.scrollHeight - el.clientHeight > 2;
      const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= rowHeightRef.current + 8;
      if ((userInteractedRef.current || loadMoreArmedRef.current) && scrollable && nearBottom) {
        userGrewRef.current = true;
        setRenderedCount((c) => (c < dataLenRef.current ? Math.min(c + batchRef.current, dataLenRef.current) : c));
      }
    },
    [captureScroller, onScrollProp, positionSentinel]
  );

  return <MaterialTableWrapper {...rest} data={renderedData} onScroll={handleScroll} onTableRefSet={handleTableRef} />;
}
LazyMaterialTable.displayName = 'LazyMaterialTable';
